import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography
} from "@material-ui/core";
import { Error as ErrorIcon, GetApp as DownloadIcon, MoreVert as MoreVertIcon, Delete as DeleteIcon } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import useWebotServer from "hooks/useWebotServer";
import { GeneratedUserCreative } from "../../../@types/sharedSchema";
import { getMakeoverEngineIcon } from "./makeoverModelOptions";

type MakeoverCardProps = {
  makeover: GeneratedUserCreative;
  onDownload?: (url: string) => void;
  onExpand?: (makeover: GeneratedUserCreative) => void;
};

/**
 * A makeover result tile: shows the AI image (or a blurred source while it
 * generates / on failure). Clicking a completed tile opens the comparison
 * gallery. A 3-dot menu offers download + delete (delete = soft-delete via backend).
 */
export function MakeoverCard({ makeover, onDownload, onExpand }: MakeoverCardProps): JSX.Element {
  const { t: translate } = useTranslation();
  const { deleteMakeover } = useWebotServer();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isCompleted = makeover.status === "completed" && !!makeover.generatedCreativeUrl;
  const isPending = makeover.status === "starting" || makeover.status === "in-progress";
  const isFailed = makeover.status === "failed";
  const sourceUrl = makeover.sourceImageUrl ?? makeover.usedImageUrls?.[0] ?? "";
  const clickable = isCompleted && !!onExpand;
  const showMenu = isCompleted || isFailed; // not while actively generating
  const engineIcon = getMakeoverEngineIcon(makeover.makeoverEngine); // tiny badge showing which engine made it

  const handleDelete = async () => {
    if (!makeover.id) return;
    setDeleting(true);
    try {
      await deleteMakeover({ makeoverId: makeover.id });
      setConfirmOpen(false);
    } catch (err) {
      console.error("Failed to delete makeover", err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card sx={{ position: "relative", overflow: "hidden" }}>
      <Box
        onClick={() => clickable && onExpand?.(makeover)}
        sx={{ position: "relative", width: "100%", paddingTop: "75%", backgroundColor: "grey.200", cursor: clickable ? "pointer" : "default" }}
      >
        {(isCompleted || sourceUrl) && (
          <Box
            component="img"
            src={isCompleted ? makeover.generatedCreativeUrl : sourceUrl}
            alt=""
            loading="lazy"
            sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", ...(isCompleted ? {} : { filter: "blur(2px)" }) }}
          />
        )}

        {isCompleted && engineIcon && (
          <Box
            sx={{
              position: "absolute",
              top: 6,
              insetInlineStart: 6,
              width: 18,
              height: 18,
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.85)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              lineHeight: 1,
              pointerEvents: "none"
            }}
          >
            <span aria-hidden>{engineIcon}</span>
          </Box>
        )}

        {isPending && (
          <Box sx={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.55)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "common.white" }}>
            <CircularProgress size={40} sx={{ color: "common.white", mb: 1 }} />
            <Typography variant="body2">{translate("makeovers.generating", "יוצר עיצוב...")}</Typography>
          </Box>
        )}

        {isFailed && (
          <Box sx={{ position: "absolute", inset: 0, backgroundColor: "rgba(244, 67, 54, 0.8)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "common.white", textAlign: "center", px: 2 }}>
            <ErrorIcon sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="body2">{translate("makeovers.failed", "היצירה נכשלה")}</Typography>
          </Box>
        )}

        {showMenu && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
            sx={{ position: "absolute", top: 8, insetInlineEnd: 8, backgroundColor: "rgba(255,255,255,0.9)", "&:hover": { backgroundColor: "common.white" } }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        {isCompleted && onDownload && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onDownload(makeover.generatedCreativeUrl);
            }}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <DownloadIcon fontSize="small" />
              <span>{translate("makeovers.download", "הורדה")}</span>
            </Stack>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setConfirmOpen(true);
          }}
        >
          <Stack direction="row" alignItems="center" gap={1} sx={{ color: "error.main" }}>
            <DeleteIcon fontSize="small" />
            <span>{translate("makeovers.delete", "מחיקה")}</span>
          </Stack>
        </MenuItem>
      </Menu>

      <Dialog open={confirmOpen} onClose={() => !deleting && setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{translate("makeovers.deleteConfirmTitle", "מחיקת עיצוב")}</DialogTitle>
        <DialogContent>
          <Typography>{translate("makeovers.deleteConfirmMessage", "למחוק את העיצוב הזה? הוא יוסר גם מהמיני-סייט. לא ניתן לשחזר.")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
            {translate("common.cancel", "ביטול")}
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={{ backgroundColor: "error.main", color: "error.contrastText", "&:hover": { backgroundColor: "error.dark" } }}
          >
            {translate("makeovers.delete", "מחיקה")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default MakeoverCard;
